import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

interface Reply {
  id: number;
  message: string;
  is_admin: boolean;
  created_at: string;
}
interface Ticket {
  id: number;
  subject: string;
  message: string;
  category: string;
  priority: string;
  status: string;
  created_at: string;
  replies_count?: number;
  replies?: Reply[];
}

const CATEGORIES: { value: string; label: string }[] = [
  { value: 'general', label: 'عام' },
  { value: 'technical', label: 'مشكلة تقنية' },
  { value: 'payment', label: 'العمولات / الدفع' },
  { value: 'report', label: 'بلاغ' },
  { value: 'suggestion', label: 'اقتراح' },
  { value: 'other', label: 'أخرى' },
];

const PRIORITIES: { value: string; label: string }[] = [
  { value: 'low', label: 'منخفضة' },
  { value: 'medium', label: 'متوسطة' },
  { value: 'high', label: 'عالية' },
];

const STATUS_MAP: Record<string, { label: string; cls: string; dot: string }> = {
  open: { label: 'مفتوحة', cls: 'bg-blue-50 text-blue-700 border-blue-200', dot: 'bg-blue-600' },
  in_progress: { label: 'قيد المعالجة', cls: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  resolved: { label: 'تم الحل', cls: 'bg-primary-50 text-primary-600 border-primary-100', dot: 'bg-primary-500' },
  closed: { label: 'مغلقة', cls: 'bg-gray-100 text-gray-500 border-gray-200', dot: 'bg-gray-400' },
};

const SEGMENTS: { v: string; l: string }[] = [
  { v: 'all', l: 'الكل' },
  { v: 'open', l: 'مفتوحة' },
  { v: 'in_progress', l: 'قيد المعالجة' },
  { v: 'resolved', l: 'تم الحل' },
  { v: 'closed', l: 'مغلقة' },
];

const catLabel = (v: string) => CATEGORIES.find((c) => c.value === v)?.label || v;
const priLabel = (v: string) => PRIORITIES.find((p) => p.value === v)?.label || v;
const priClass = (v: string) => (v === 'high' ? 'text-red-600 font-bold' : v === 'medium' ? 'text-amber-600 font-bold' : 'text-gray-500');

const fmtDate = (s: string) => {
  try { return new Date(s).toLocaleString('ar-SA', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return s; }
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const s = STATUS_MAP[status] || STATUS_MAP.open;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full border whitespace-nowrap ${s.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />{s.label}
    </span>
  );
};

const SupportTicketsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading } = useAuth();

  const [mode, setMode] = useState<'list' | 'new' | 'view'>('list');
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [q, setQ] = useState('');

  // إنشاء تذكرة
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('general');
  const [priority, setPriority] = useState('medium');
  const [message, setMessage] = useState('');
  const [creating, setCreating] = useState(false);

  // عرض تذكرة
  const [active, setActive] = useState<Ticket | null>(null);
  const [loadingView, setLoadingView] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);

  // بوابة: للبائعين فقط
  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      toast.error('يجب تسجيل الدخول');
      navigate('/login');
    } else if (user && user.is_seller === false) {
      toast.error('الدعم الفني متاح للبائعين فقط');
      navigate('/my/seller-profile?redirect=/my/support');
    }
  }, [isAuthenticated, isLoading, user, navigate]);

  const loadTickets = useCallback(async () => {
    try {
      setLoadingList(true);
      const res = await api.get('/my/support-tickets');
      const payload = res.data?.data;
      const list = Array.isArray(payload) ? payload : (payload?.data || []);
      setTickets(list);
    } catch {
      // متروك للخادم
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && user?.is_seller !== false) loadTickets();
  }, [isAuthenticated, user, loadTickets]);

  const openTicket = async (id: number) => {
    setMode('view');
    setActive(null);
    setLoadingView(true);
    try {
      const res = await api.get(`/support-tickets/${id}`);
      setActive(res.data.data);
    } catch {
      toast.error('تعذّر فتح التذكرة');
      setMode('list');
    } finally {
      setLoadingView(false);
    }
  };

  const createTicket = async () => {
    if (!subject.trim()) { toast.error('أدخل موضوع التذكرة'); return; }
    if (!message.trim()) { toast.error('اكتب رسالتك'); return; }
    try {
      setCreating(true);
      await api.post('/support-tickets', { subject, message, category, priority });
      toast.success('تم إنشاء التذكرة');
      setSubject(''); setMessage(''); setCategory('general'); setPriority('medium');
      setMode('list');
      loadTickets();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'تعذّر إنشاء التذكرة');
    } finally {
      setCreating(false);
    }
  };

  const sendReply = async () => {
    if (!active) return;
    if (!replyText.trim()) { toast.error('اكتب ردك'); return; }
    try {
      setReplying(true);
      await api.post(`/support-tickets/${active.id}/reply`, { message: replyText });
      setReplyText('');
      const res = await api.get(`/support-tickets/${active.id}`);
      setActive(res.data.data);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'تعذّر إرسال الرد');
    } finally {
      setReplying(false);
    }
  };

  if (!isAuthenticated || user?.is_seller === false) return null;

  const counts = {
    open: tickets.filter((t) => t.status === 'open').length,
    in_progress: tickets.filter((t) => t.status === 'in_progress').length,
    resolved: tickets.filter((t) => t.status === 'resolved').length,
    closed: tickets.filter((t) => t.status === 'closed').length,
  };

  const visible = tickets.filter((t) =>
    (statusFilter === 'all' || t.status === statusFilter) &&
    (!q.trim() || t.subject.includes(q.trim()) || (t.message || '').includes(q.trim()))
  );

  const StatPill: React.FC<{ n: number; label: string; dot: string; text: string }> = ({ n, label, dot, text }) => (
    <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-xs font-semibold text-gray-500">
      <span className={`w-2 h-2 rounded-full ${dot}`} />
      <b className={`text-[15px] font-extrabold ${text}`}>{n}</b> {label}
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto px-0 py-2" dir="rtl">

      {/* ===== قائمة التذاكر ===== */}
      {mode === 'list' && (
        <>
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-extrabold text-gray-800">الدعم الفني</h1>
            <button onClick={() => setMode('new')}
              className="bg-primary-500 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-primary-600 transition inline-flex items-center gap-1.5">
              ＋ فتح تذكرة
            </button>
          </div>

          {/* شرائح الحالة */}
          <div className="flex gap-2 flex-wrap mb-4">
            <StatPill n={counts.open} label="مفتوحة" dot="bg-blue-600" text="text-blue-600" />
            <StatPill n={counts.in_progress} label="قيد المعالجة" dot="bg-amber-500" text="text-amber-600" />
            <StatPill n={counts.resolved} label="تم الحل" dot="bg-primary-500" text="text-primary-600" />
            <StatPill n={counts.closed} label="مغلقة" dot="bg-gray-400" text="text-gray-500" />
          </div>

          {/* أدوات */}
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="🔍 ابحث في تذاكرك…"
              className="flex-1 min-w-[180px] max-w-xs bg-white border border-gray-200 rounded-lg px-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            <div className="flex bg-white border border-gray-200 rounded-lg overflow-hidden">
              {SEGMENTS.map((s) => (
                <button key={s.v} onClick={() => setStatusFilter(s.v)}
                  className={`px-3 py-2 text-xs font-semibold transition ${statusFilter === s.v ? 'bg-primary-50 text-primary-600' : 'text-gray-500 hover:bg-gray-50'}`}>
                  {s.l}
                </button>
              ))}
            </div>
          </div>

          {loadingList ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary-500 border-t-transparent" />
            </div>
          ) : visible.length === 0 ? (
            <div className="bg-white rounded-xl p-10 text-center shadow-sm border border-gray-100">
              <span className="text-4xl">🎫</span>
              <p className="text-gray-500 mt-3 text-sm">
                {tickets.length === 0 ? 'ما عندك تذاكر بعد' : 'لا توجد تذاكر بهذا الفلتر'}
              </p>
              {tickets.length === 0 && (
                <button onClick={() => setMode('new')}
                  className="mt-4 bg-primary-500 text-white px-5 py-2.5 rounded-lg text-sm font-bold hover:bg-primary-600 transition">
                  افتح تذكرة جديدة
                </button>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-2.5">
              {visible.map((t) => (
                <button key={t.id} onClick={() => openTicket(t.id)}
                  className="w-full flex items-center gap-3 bg-white rounded-xl border border-gray-200 hover:border-primary-200 hover:shadow-sm transition p-3.5 text-right">
                  <span className="text-xs font-extrabold text-gray-300 shrink-0">#{t.id}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-gray-800 truncate">{t.subject}</div>
                    <div className="text-xs text-gray-500 mt-0.5 truncate">
                      {catLabel(t.category)}
                      <span className="text-gray-300 mx-1.5">•</span>
                      <span className={priClass(t.priority)}>أولوية {priLabel(t.priority)}</span>
                      <span className="text-gray-300 mx-1.5">•</span>
                      {fmtDate(t.created_at)}
                    </div>
                  </div>
                  <StatusBadge status={t.status} />
                  <span className="text-gray-300 text-lg shrink-0">‹</span>
                </button>
              ))}
            </div>
          )}

          {!loadingList && visible.length > 0 && (
            <div className="text-xs text-gray-400 mt-4">عرض {visible.length} من {tickets.length} تذكرة</div>
          )}
        </>
      )}

      {/* ===== تذكرة جديدة ===== */}
      {mode === 'new' && (
        <>
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-extrabold text-gray-800">تذكرة جديدة</h1>
            <button onClick={() => setMode('list')} className="text-sm text-gray-500 hover:text-gray-700 font-semibold">→ رجوع للتذاكر</button>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 space-y-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1.5">الموضوع *</label>
              <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200}
                placeholder="موضوع التذكرة"
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1.5">التصنيف</label>
                <select value={category} onChange={(e) => setCategory(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1.5">الأولوية</label>
                <select value={priority} onChange={(e) => setPriority(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                  {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1.5">الرسالة *</label>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={6} maxLength={5000}
                placeholder="اشرح مشكلتك أو استفسارك بالتفصيل..."
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none" />
            </div>
            <div className="flex gap-3">
              <button onClick={createTicket} disabled={creating}
                className="flex-1 bg-primary-500 text-white py-3 rounded-lg font-bold text-sm hover:bg-primary-600 transition disabled:opacity-50">
                {creating ? 'جاري الإرسال...' : 'إرسال التذكرة'}
              </button>
              <button onClick={() => setMode('list')}
                className="px-6 bg-gray-100 text-gray-600 py-3 rounded-lg font-semibold text-sm hover:bg-gray-200 transition">
                إلغاء
              </button>
            </div>
          </div>
        </>
      )}

      {/* ===== عرض التذكرة + المحادثة ===== */}
      {mode === 'view' && (
        loadingView ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary-500 border-t-transparent" />
          </div>
        ) : active ? (
          <>
            <div className="flex items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-2.5 min-w-0">
                <button onClick={() => { setMode('list'); setActive(null); }}
                  className="bg-white border border-gray-200 text-gray-600 px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-gray-50 transition whitespace-nowrap">
                  → رجوع للتذاكر
                </button>
                <h1 className="text-base font-extrabold text-gray-800 truncate">{active.subject}</h1>
              </div>
            </div>

            {/* شريط معلومات نحيف */}
            <div className="flex items-center justify-between gap-3 flex-wrap bg-gray-50 border border-gray-200 rounded-lg px-3.5 py-2.5 mb-4 text-xs">
              <div className="flex items-center gap-3.5 flex-wrap">
                <span className="font-bold text-gray-800"><span className="text-gray-400 font-semibold">التذكرة</span> <bdi>#{active.id}</bdi></span>
                <span className="w-px h-3.5 bg-gray-200" />
                <span className="font-bold text-gray-800"><span className="text-gray-400 font-semibold">التصنيف</span> {catLabel(active.category)}</span>
                <span className="w-px h-3.5 bg-gray-200" />
                <span className="font-bold text-gray-800"><span className="text-gray-400 font-semibold">الأولوية</span> <span className={priClass(active.priority)}>{priLabel(active.priority)}</span></span>
              </div>
              <StatusBadge status={active.status} />
            </div>

            {/* المحادثة — بطاقات موحّدة العرض للتناسق */}
            <div className="flex flex-col gap-3 mb-4">
              {[
                { message: active.message, staff: false, created_at: active.created_at, key: 'root' },
                ...(active.replies || []).map((r) => ({ message: r.message, staff: r.is_admin, created_at: r.created_at, key: `r${r.id}` })),
              ].map((m) => (
                <div key={m.key} className={`rounded-xl border overflow-hidden ${m.staff ? 'border-gray-200' : 'border-primary-100'}`}>
                  <div className={`flex items-center justify-between px-4 py-2.5 ${m.staff ? 'bg-slate-50' : 'bg-primary-50'}`}>
                    <div className="flex items-center gap-2">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${m.staff ? 'bg-slate-600' : 'bg-primary-500'}`}>
                        {m.staff ? 'دعم' : 'أنت'}
                      </div>
                      <span className={`text-xs font-bold ${m.staff ? 'text-slate-700' : 'text-primary-600'}`}>
                        {m.staff ? 'الدعم الفني' : 'أنت'}
                      </span>
                    </div>
                    <span className="text-[10px] text-gray-400">{fmtDate(m.created_at)}</span>
                  </div>
                  <div className="px-4 py-3 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed bg-white">{m.message}</div>
                </div>
              ))}
            </div>

            {active.status === 'closed' ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center text-xs text-gray-500">
                هذه التذكرة مغلقة
              </div>
            ) : (
              <div className="bg-white rounded-xl p-3.5 shadow-sm border border-gray-100">
                <div className="text-xs font-bold text-gray-600 mb-2">↩︎ إضافة رد</div>
                <textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} rows={3} maxLength={5000}
                  placeholder="اكتب ردك هنا…"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none" />
                <div className="flex justify-start mt-2.5">
                  <button onClick={sendReply} disabled={replying}
                    className="bg-primary-500 text-white px-6 py-2.5 rounded-lg text-sm font-bold hover:bg-primary-600 transition disabled:opacity-50">
                    {replying ? 'جاري الإرسال...' : 'إرسال الرد'}
                  </button>
                </div>
              </div>
            )}
          </>
        ) : null
      )}
    </div>
  );
};

export default SupportTicketsPage;
